'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Dock presence (MOTION-SPEC §9).
 *
 * Shown 3000 ms after load and after every scene change (not while touring),
 * on any pointer move, while the pointer rests in the bottom 120 px zone, and
 * while the shell says `visible` or a hold (persona popover open, tour paused)
 * is active. Hidden 2400 ms after the last move by one timer that re-arms
 * itself when a newer move arrived. Pinned (never hidden) on coarse pointers.
 *
 * The pointer listener only writes refs; React state flips once per
 * hidden ↔ shown transition and once per edge-zone crossing.
 */

export type DockPresence = 'pinned' | 'shown' | 'hidden';

export interface DockPresenceOptions {
  visible: boolean;
  pinned: boolean;
  hold: boolean;
  sceneIndex: number;
  touring: boolean;
}

const HIDE_AFTER_MS = 2400;
const HOLD_AFTER_MS = 3000;
const EDGE_ZONE_PX = 120;

export function useDockPresence({ visible, pinned, hold, sceneIndex, touring }: DockPresenceOptions): {
  state: DockPresence;
  wake: () => void;
} {
  const [awake, setAwake] = useState(true);
  const [nearEdge, setNearEdge] = useState(false);
  const awakeRef = useRef(true);
  const nearRef = useRef(false);
  const lastMove = useRef(0);
  const holdUntil = useRef(0);
  const timer = useRef<number | null>(null);
  const touringRef = useRef(touring);
  const prevScene = useRef(sceneIndex);

  useEffect(() => {
    touringRef.current = touring;
  }, [touring]);

  const arm = useCallback(function arm() {
    if (timer.current !== null) return;
    const due = Math.max(lastMove.current + HIDE_AFTER_MS, holdUntil.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      const again = Math.max(lastMove.current + HIDE_AFTER_MS, holdUntil.current);
      if (performance.now() >= again - 4) {
        awakeRef.current = false;
        setAwake(false);
      } else {
        arm();
      }
    }, Math.max(0, due - performance.now()));
  }, []);

  const wake = useCallback(() => {
    lastMove.current = performance.now();
    if (!awakeRef.current) {
      awakeRef.current = true;
      setAwake(true);
    }
    arm();
  }, [arm]);

  // Shown for 3000 ms after load.
  useEffect(() => {
    if (pinned) return;
    holdUntil.current = performance.now() + HOLD_AFTER_MS;
    awakeRef.current = true;
    setAwake(true);
    arm();
    return () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [pinned, arm]);

  // Shown for 3000 ms after every scene change, unless the tour is playing.
  useEffect(() => {
    if (prevScene.current === sceneIndex) return;
    prevScene.current = sceneIndex;
    if (pinned || touringRef.current) return;
    holdUntil.current = performance.now() + HOLD_AFTER_MS;
    wake();
  }, [sceneIndex, pinned, wake]);

  // Pointer bus: refs only, state once per transition.
  useEffect(() => {
    if (pinned) return;
    const onMove = (event: PointerEvent) => {
      lastMove.current = performance.now();
      if (!awakeRef.current) {
        awakeRef.current = true;
        setAwake(true);
      }
      if (timer.current === null) arm();
      const near = event.clientY >= window.innerHeight - EDGE_ZONE_PX;
      if (near !== nearRef.current) {
        nearRef.current = near;
        setNearEdge(near);
      }
    };
    const onOut = (event: PointerEvent) => {
      if (event.relatedTarget === null && nearRef.current) {
        nearRef.current = false;
        setNearEdge(false);
      }
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerout', onOut, { passive: true });
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerout', onOut);
    };
  }, [pinned, arm]);

  const state: DockPresence = pinned ? 'pinned' : visible || awake || nearEdge || hold ? 'shown' : 'hidden';
  return { state, wake };
}
